import React, { useState, useEffect, useCallback } from 'react';
import { X, Shield, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const ExitIntentPopup = () => {
  const [show, setShow] = useState(false);

  const trigger = useCallback(() => {
    if (!sessionStorage.getItem('exitPopupShown')) {
      setShow(true);
      sessionStorage.setItem('exitPopupShown', 'true');
    }
  }, []);

  useEffect(() => {
    // Desktop: mouseleave
    const handleMouseLeave = (e) => {
      if (e.clientY <= 5) trigger();
    };

    // Mobile: scroll up rapidly after scrolling down
    let lastScrollY = 0;
    let maxScrollY = 0;
    const handleScroll = () => {
      const y = window.scrollY;
      if (y > maxScrollY) maxScrollY = y;
      if (maxScrollY > 600 && y < maxScrollY - 300 && y < lastScrollY) {
        trigger();
      }
      lastScrollY = y;
    };

    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
      window.addEventListener('scroll', handleScroll, { passive: true });
    }, 5000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [trigger]);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-300 p-4"
      data-testid="exit-intent-overlay"
      onClick={() => setShow(false)}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-400"
        onClick={(e) => e.stopPropagation()}
        data-testid="exit-intent-popup"
      >
        <div className="bg-[#101C47] p-5 sm:p-6 text-center relative">
          <button
            onClick={() => setShow(false)}
            className="absolute top-3 right-3 text-white/60 hover:text-white transition-colors"
            data-testid="exit-intent-close"
            aria-label="Close popup"
          >
            <X size={20} />
          </button>
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <Shield size={24} className="sm:w-8 sm:h-8 text-white" />
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-white mb-1">Wait — Don't Leave Unprotected!</h2>
          <p className="text-xs sm:text-sm text-gray-300">Most homeowners overpay by 20% on insurance.</p>
        </div>

        <div className="p-5 sm:p-6">
          <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-5 text-center">
            Take our 60-second coverage quiz and discover if you could save hundreds per year.
          </p>

          <div className="space-y-2.5 sm:space-y-3">
            <Link
              to="/find-my-coverage"
              onClick={() => setShow(false)}
              className="flex items-center justify-center gap-2 bg-[#f92626] hover:bg-[#d91f1f] text-white font-semibold py-2.5 sm:py-3 px-6 rounded-lg transition-colors w-full text-sm"
              data-testid="exit-intent-quiz-btn"
            >
              Find My Coverage
              <ArrowRight size={16} />
            </Link>
            <a
              href="tel:706-434-8184"
              className="flex items-center justify-center gap-2 border-2 border-[#101C47] text-[#101C47] hover:bg-[#101C47] hover:text-white font-semibold py-2.5 sm:py-3 px-6 rounded-lg transition-colors w-full text-sm"
              data-testid="exit-intent-call-btn"
            >
              Call 706-434-8184 Now
            </a>
          </div>

          <p className="text-[10px] text-gray-400 text-center mt-3 sm:mt-4">No spam. Free quote. Cancel anytime.</p>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
